import { isAxiosError } from "axios";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { useResyncEmployees } from "@/hooks/use-se-triggers";
import { ConfirmButton } from "./confirm-button";
import { ToolCard } from "./tool-card";

/** Syncs the page cannot run until backoffice-api can reach them. */
const UNAVAILABLE_SYNCS = [
  {
    name: "MDM / EDR",
    reason: "Triggered only by an SQS message; no HTTP route to proxy.",
  },
  {
    name: "Knowledge base",
    reason: "kb-integration-subscriber is not on the backoffice proxy allowlist.",
  },
  {
    name: "Applications",
    reason: "applications-collector and -processor are not on the backoffice proxy allowlist.",
  },
];

export function ResyncTool({ tenantId }: { tenantId: string }) {
  const resyncEmployees = useResyncEmployees(tenantId);

  // The sync always outlasts the proxy's 30s timeout, so the page only reports
  // that it started; a timeout is not a failure (see runTrigger)
  const onResyncEmployees = () => {
    toast.success("Employee sync started. It runs in the background.");
    resyncEmployees.mutate(undefined, {
      onError: (err) => {
        const detail = isAxiosError<{ detail?: unknown }>(err)
          ? err.response?.data.detail
          : undefined;
        toast.error(
          `Employee sync failed: ${typeof detail === "string" ? detail : "the request was rejected"}`,
        );
      },
    });
  };

  return (
    <ToolCard
      icon={RefreshCw}
      title="Resync an integration"
      description="Runs an integration's sync now instead of waiting for its schedule."
    >
      <div className="divide-y rounded-md border">
        <div className="flex items-center justify-between gap-4 p-3">
          <div className="space-y-0.5">
            <p className="text-sm font-medium">Employees (HRIS / IDP)</p>
            <p className="text-muted-foreground text-xs">
              Re-pulls employees from every connected HRIS and IDP. The
              scheduled run is daily at 07:15 UTC.
            </p>
          </div>
          <ConfirmButton
            label="Resync"
            title="Resync this tenant's employees?"
            description="Pulls every HRIS and IDP connection now. Employees are created, updated or removed to match the providers, exactly as the daily sync does."
            confirmLabel="Resync employees"
            pending={resyncEmployees.isPending}
            onConfirm={onResyncEmployees}
          />
        </div>
        {UNAVAILABLE_SYNCS.map((sync) => (
          <div key={sync.name} className="flex items-center justify-between gap-4 p-3">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">{sync.name}</p>
              <p className="text-muted-foreground text-xs">{sync.reason}</p>
            </div>
            <Badge variant="secondary">Coming soon</Badge>
          </div>
        ))}
      </div>
    </ToolCard>
  );
}
